import type { NewReport } from '../../api/monitor';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { useReports } from '../../hooks/useReports';
import { ReportForm } from './ReportForm';
import { ReportRow } from './ReportRow';

/**
 * Tela de Relatórios de consumo.
 *
 * A geração é assíncrona: o pedido volta na hora e a lista acompanha o estado
 * até virar "Pronto". A planilha não fica guardada em lugar nenhum — o que o
 * servidor guarda é a leitura de consumo, e o XLSX é montado a cada clique em
 * "Baixar".
 *
 * O controller nasce aqui, e não no App, pelo mesmo motivo da tela de Linhas:
 * a lista só interessa a esta tela e subir o hook faria o app buscar
 * relatórios já no login.
 */
export function ReportsScreen({ userEmail }: { userEmail: string | null }) {
  const controller = useReports();
  const { options, accounts, reports, loading, error, creating, masterId } = controller;
  const { toast, show } = useToast();

  const handleSubmit = async (payload: NewReport) => {
    await controller.create(payload);
    show('Relatório solicitado. Ele aparece como "Pronto" assim que a coleta terminar.');
  };

  if (loading && !options) {
    return <LoadingCard message="Carregando os filtros do relatório…" />;
  }

  if (error && !options) {
    return (
      <ErrorCard
        title="Não foi possível carregar a tela de relatórios."
        detail={error}
        onRetry={controller.reload}
      />
    );
  }

  if (!options) return null;

  return (
    <>
      {/* Sem o SGC o relatório sai, só que com a cota do portal no lugar do
          contratado. */}
      {!options.franchiseConfigured ? (
        <div className="border-warn-200 bg-warn-100 text-warn-700 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          Sem <code>SGC_API_URL</code>, a franquia do relatório vem da cota do portal da operadora,
          e não do que a linha contratou. O consumo continua correto; o percentual, não
          necessariamente.
        </div>
      ) : null}

      {options.masters.length === 0 ? (
        <div className="border-line-200 text-ink-600 rounded-[14px] border bg-white p-7 text-center text-[13.5px]">
          Nenhum master tem conta de operadora ativa vinculada, então não há o que relatar.
        </div>
      ) : (
        <ReportForm
          options={options}
          accounts={accounts}
          masterId={masterId}
          creating={creating}
          onSelectMaster={controller.selectMaster}
          onSubmit={handleSubmit}
          requestedBy={userEmail}
        />
      )}

      <div className="mt-4">
        <div className="mb-2 text-[14px] font-extrabold">Relatórios gerados</div>

        <div className="border-line-200 overflow-hidden rounded-[14px] border bg-white">
          {reports.length > 0 ? (
            reports.map((report) => <ReportRow key={report.id} report={report} />)
          ) : (
            <div className="text-ink-600 p-7 text-center text-[13.5px]">
              Nenhum relatório gerado ainda.
            </div>
          )}
        </div>
      </div>

      <Toast message={toast} />
    </>
  );
}
